import { Compass } from 'lucide-react'

import { isDemoPresentation } from '@/lib/constants'

import { useGuidedTourPostHog } from '@/features/guided-tour/hooks/use-tour-posthog'

import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/components/ui/sidebar'

import { DEMO_TOUR_ID } from '../id'
import { startDemoTour } from '../loader'

/**
 * Deliberately NOT read from the tour chunk's `copy.ts`: this button is
 * statically imported by the app sidebar, so importing that module here would
 * drag all 16 stops of marketing copy back into the shared graph the loader's
 * dynamic import exists to keep them out of.
 */
const TAKE_TOUR_LABEL = 'Take tour'

/**
 * "Take tour" — the demo's way back into the guided tour, first in the sidebar
 * so a visitor who skipped (or wandered) is never more than one click from the
 * narrative. Mirrors `WhatsNewSidebarButton`'s sidebar-group shape, but its
 * pulse is deliberately persistent (see the ripple comment below).
 *
 * Self-gates on `isDemoPresentation`, so it renders nothing in `dev:mock` and
 * production. The copy string is the only thing it pulls from the demo chunk's
 * neighbourhood — the 16 steps themselves stay behind `startDemoTour`'s folded
 * dynamic import.
 */
export function DemoTourSidebarButton() {
	const { trackStarted } = useGuidedTourPostHog()

	if (!isDemoPresentation) return null

	return (
		<SidebarGroup>
			<SidebarMenu>
				<SidebarMenuItem>
					<SidebarMenuButton
						data-testid="demo-take-tour"
						tooltip={TAKE_TOUR_LABEL}
						onClick={() => {
							trackStarted({ tourId: DEMO_TOUR_ID, source: 'demo-sidebar' })
							void startDemoTour()
						}}
					>
						{/* Pulses persistently by design: unlike the product's
						    `!seen`-gated TourTrigger, the demo's way back into the
						    narrative should keep drawing the eye for the whole session,
						    not go calm after one viewing. */}
						<span className="text-sidebar-primary beta-ripple relative inline-flex">
							<Compass />
						</span>
						<span>{TAKE_TOUR_LABEL}</span>
					</SidebarMenuButton>
				</SidebarMenuItem>
			</SidebarMenu>
		</SidebarGroup>
	)
}
